import { mergeMessage, parseDotNetTimeSpan } from '../transcript';
import type { CallTranscript as MergeableTranscript, CallTranscriptMessage } from '../transcript';
import type {
  CallNotificationItem,
  CallTranscript,
  CallTranscriptEvent,
  CallTranscriptMessage as WireCallTranscriptMessage,
  IsoDateTimeString,
} from '../realtime/contracts';

export type ParsedTranscriptEvent = Omit<CallTranscriptEvent, 'offset'> & { offset: number };

export type ParsedTranscript = {
  messages: CallTranscriptMessage[];
  detectedEvents: ParsedTranscriptEvent[];
  summary: string | null;
};

export type IncidentCall = {
  callId: string;
  startTimeUtc: IsoDateTimeString;
  endTimeUtc: IsoDateTimeString | null;
  callTranscript: ParsedTranscript | null;
};

const EMPTY_TRANSCRIPT: ParsedTranscript = { messages: [], detectedEvents: [], summary: null };

export function parseTranscriptMessage(message: WireCallTranscriptMessage): CallTranscriptMessage {
  return {
    startOffset: parseDotNetTimeSpan(message.startOffset),
    endOffset: parseDotNetTimeSpan(message.endOffset),
    source: message.source,
    originalContent: message.originalContent,
    content: message.content,
    type: message.type,
    recognizedWords:
      message.recognizedWords?.map((word) => ({
        word: word.word,
        start: parseDotNetTimeSpan(word.start),
        end: parseDotNetTimeSpan(word.end),
      })) ?? null,
    messageId: message.messageId ?? null,
    parentMessageId: message.parentMessageId ?? null,
  };
}

export function parseTranscript(transcript: CallTranscript): ParsedTranscript {
  return {
    messages: (transcript.messages ?? []).map(parseTranscriptMessage),
    detectedEvents: (transcript.detectedEvents ?? []).map((event) => ({
      ...event,
      offset: parseDotNetTimeSpan(event.offset),
    })),
    summary: transcript.summary ?? null,
  };
}

export function toIncidentCall(call: CallNotificationItem): IncidentCall {
  return {
    callId: call.callId,
    startTimeUtc: call.startTimeUtc,
    endTimeUtc: call.endTimeUtc ?? null,
    callTranscript: call.callTranscript == null ? null : parseTranscript(call.callTranscript),
  };
}

export type IncidentCallUpdate =
  | { type: 'call-started'; call: CallNotificationItem }
  | { type: 'call-ended'; endTimeUtc: IsoDateTimeString }
  | { type: 'transcript-replaced'; transcript: CallTranscript }
  | { type: 'transcript-message-updated'; message: WireCallTranscriptMessage };

export function applyCallUpdate(
  current: IncidentCall | null,
  update: IncidentCallUpdate,
): IncidentCall | null {
  switch (update.type) {
    case 'call-started': {
      const started = toIncidentCall(update.call);
      if (current === null || Date.parse(started.startTimeUtc) >= Date.parse(current.startTimeUtc)) {
        return started;
      }
      return current;
    }

    case 'call-ended':
      return current === null ? null : { ...current, endTimeUtc: update.endTimeUtc };

    case 'transcript-replaced':
      return current === null ? null : { ...current, callTranscript: parseTranscript(update.transcript) };

    case 'transcript-message-updated': {
      if (current === null) {
        return null;
      }

      const transcript = current.callTranscript ?? EMPTY_TRANSCRIPT;
      const merged = mergeMessage(
        transcript as unknown as MergeableTranscript,
        parseTranscriptMessage(update.message),
      ) as unknown as ParsedTranscript;

      return { ...current, callTranscript: merged };
    }
  }
}

export function getCurrentTranscriptOffset(transcript: ParsedTranscript): number | null {
  const latestMessageOffset =
    transcript.messages.length === 0 ? null : Math.max(...transcript.messages.map((message) => message.endOffset));
  const latestEventOffset =
    transcript.detectedEvents.length === 0
      ? null
      : Math.max(...transcript.detectedEvents.map((event) => event.offset));

  if (latestMessageOffset === null) {
    return latestEventOffset;
  }
  if (latestEventOffset === null) {
    return latestMessageOffset;
  }
  return latestMessageOffset > latestEventOffset ? latestMessageOffset : latestEventOffset;
}
